import { HOURS, hourlyProfile, legacyHourlyProfile } from "./dailyConsumptionModel";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SEASONS = [0, 0, 1, 1, 1, 2, 2, 2, 3, 3, 3, 0];
const INTENSITY = { Commercial: [1, .85, 1.25, .9], "Residential Urban": [1.15, .85, 1.1, .9], "Residential Rural": [1.25, .85, 1, .9] };
const KINDS = ["weekdays", "saturdays", "sundays"];
const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

// Mirrors demand_periods.py: populated single months override aggregates. This is
// read-only; never replace saved inputs with these expanded monthly values.
export function previewMonthlyEnergy(monthly, year, useCase) {
  const entries = Object.entries(monthly || {}).flatMap(([key, raw]) => {
    if (raw == null || String(raw).trim() === "" || !Number.isFinite(Number(raw)) || Number(raw) < 0) return [];
    const ends = key.split("-").map((s) => MONTHS.findIndex((m) => m.toLowerCase() === s.trim().slice(0, 3).toLowerCase()));
    if (ends.some((i) => i < 0) || ends.length > 2) return [];
    const length = ends.length === 1 ? 1 : (ends[1] - ends[0] + 12) % 12 + 1;
    return [{ months: Array.from({ length }, (_, i) => (ends[0] + i) % 12), energy: Number(raw) }];
  });
  const hasMonths = entries.some((e) => e.months.length === 1);
  const output = Array(12).fill(null);
  const intensity = INTENSITY[useCase] || INTENSITY.Commercial;
  entries.filter((e) => !hasMonths || e.months.length === 1).forEach(({ months, energy }) => {
    const weights = months.map((m) => daysInMonth(year, m) * intensity[SEASONS[m]]);
    const total = weights.reduce((a, b) => a + b, 0);
    months.forEach((m, i) => { output[m] = (output[m] || 0) + energy * weights[i] / total; });
  });
  return output;
}

function hoursFor(window) {
  const read = (raw) => raw == null || raw === "" || raw === "None" ? null : Number(String(raw).split(":")[0]);
  const start = read(window?.startHour), end = read(window?.endHour);
  if (start === null || end === null || start === end) return [];
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || start > 23 || end < 0 || end > 24) return null;
  return HOURS.filter((h) => start < end ? h >= start && h < end : h >= start || h < end);
}

// Illustrative monthly mean days, BEFORE holidays and synthetic variation.
// Same deterministic allocation and base-load formula as demand_shape.py.
// Deliberately not presented as the simulation's final hourly time series.
export function appliedDemandPreview(demand, useCase, year, month, includeBaseLoad = false) {
  const shape = demand.shape;
  if (shape?.version !== 1 || !Number.isInteger(year) || year < 1900 || year > 2200) return null;
  const energy = previewMonthlyEnergy(demand.monthly, year, useCase)[month];
  if (energy === null) return null;
  const season = ["winter", "spring", "summer", "autumn"][shape.seasonal ? SEASONS[month] : 0];
  const days = daysInMonth(year, month);
  const counts = [0, 0, 0];
  for (let day = 1; day <= days; day++) {
    const wd = new Date(Date.UTC(year, month, day)).getUTCDay();
    counts[wd === 0 ? 2 : wd === 6 ? 1 : 0]++;
  }
  const windows = KINDS.map((kind) => shape.useSchedule ? hoursFor(demand.schedule?.[kind]) : HOURS);
  if (windows.some((hours) => hours === null)) return null;
  const weights = KINDS.map((kind, i) => windows[i].length ? (shape.dayMode === "schedule" ? windows[i].length : Number(shape.dayWeights?.[season]?.[kind])) : 0);
  if (weights.some((w) => !Number.isFinite(w) || w < 0)) return null;
  const total = weights.reduce((sum, weight, i) => sum + weight * counts[i], 0);
  if (energy > 0 && total <= 0) return null;
  const rawBase = includeBaseLoad ? Number(demand.baseLoadKw || 0) : 0;
  if (!Number.isFinite(rawBase) || rawBase < 0) return null;
  const base = Math.min(rawBase, energy / (days * 24));
  const remaining = energy - base * days * 24;
  const curves = KINDS.map((kind, i) => {
    const hours = windows[i];
    const explicit = shape.dayProfiles?.[kind === "weekdays" ? "weekdays" : "weekends"];
    let profile;
    if (explicit) {
      profile = hourlyProfile(explicit);
      if (!profile) return null;
    } else {
      const legacy = legacyHourlyProfile(demand.weights, useCase);
      const blockCounts = [0, 1, 2, 3].map((b) => hours.filter((h) => Math.floor(h / 6) === b).length);
      profile = legacy.map((v, h) => blockCounts[Math.floor(h / 6)] ? v * 6 / blockCounts[Math.floor(h / 6)] : 0);
    }
    const live = hours.reduce((sum, h) => sum + profile[h], 0);
    const dayEnergy = total ? remaining * weights[i] / total : 0;
    return HOURS.map((h) => base + (hours.includes(h) ? dayEnergy * (live > 0 ? profile[h] / live : 1 / hours.length) : 0));
  });
  if (curves.some((curve) => !curve)) return null;
  return { counts, energy, capped: rawBase > base, rows: HOURS.map((h) => ({ hour: `${String(h).padStart(2, "0")}:00`, weekdays: curves[0][h], saturdays: curves[1][h], sundays: curves[2][h] })) };
}
